import React, { useState, useMemo } from "react";
import { ArrowLeft, Landmark, Plus, X, Layers, Link2 } from "lucide-react";
import AddExistingAssetModal from "./AddExisitingAssetModal";
import AddNewAssetModal from "./AddNewAssetModal";
import UpdateLoanModal from "./AddLoanModal";
import { useAppSelector } from "../../../app/store";
import { selectAssetsForCharge, selectChargeById, selectCreditAccountsForCharge } from "../Logic/chargesSlice";
import { CHARGE_MODE_LABELS, DISTRIBUTION_RIGHTS_LABELS, SECURITY_ROLE_LABELS } from "../../../api/charges";
import { ASSET_CLASS_LABELS, ASSET_TYPE_LABELS, Asset } from "../../../api/assets";

enum ManageChargeView {
  OVERVIEW = "OVERVIEW",
  ADD_EXISTING_ASSET = "ADD_EXISTING_ASSET",
  ADD_NEW_ASSET = "ADD_NEW_ASSET",
  ADD_LOAN = "ADD_LOAN",
}

interface ManageChargeModalProps {
  chargeId: string;
  onBack: () => void;
  onClose: () => void;
}

const ManageChargeModal: React.FC<ManageChargeModalProps> = ({ chargeId, onBack, onClose }) => {
  const [view, setView] = useState<ManageChargeView>(ManageChargeView.OVERVIEW);

  const charge = useAppSelector(state => selectChargeById(state, chargeId));
  const assets = useAppSelector(state => selectAssetsForCharge(state, chargeId));
  const creditAccounts = useAppSelector(state => selectCreditAccountsForCharge(state, chargeId));

  const groupedAssets = useMemo(() => {
    return assets.reduce<Record<string, Asset[]>>((grouped, asset) => {
      const assetClass = asset.asset_class;
      if (!grouped[assetClass]) {
        grouped[assetClass] = [];
      }
      grouped[assetClass].push(asset);
      return grouped;
    }, {});
  }, [assets]);

  const securityRole = charge ? SECURITY_ROLE_LABELS[charge.charges.security_role!] : "";
  const chargeMode = charge ? CHARGE_MODE_LABELS[charge.charges.charge_mode!] : "";
  const distributionRights = charge ? DISTRIBUTION_RIGHTS_LABELS[charge.charges.distribution_rights!] : "";
  const assetCount = charge?.asset_linkages.length ?? 0;
  const loanCount = charge?.loan_linkages.length ?? 0;

  const handleBackToOverview = () => setView(ManageChargeView.OVERVIEW);

  if (view === ManageChargeView.ADD_EXISTING_ASSET) {
    return <AddExistingAssetModal chargeId={chargeId} onBack={handleBackToOverview} onClose={onClose} />;
  }
  if (view === ManageChargeView.ADD_NEW_ASSET) {
    return <AddNewAssetModal chargeId={chargeId} onBack={handleBackToOverview} onClose={onClose} />;
  }
  if (view === ManageChargeView.ADD_LOAN) {
    return <UpdateLoanModal chargeId={chargeId} onBack={handleBackToOverview} onClose={onClose} />;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(10,10,10,0.20)]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[608px] max-h-[90vh] flex flex-col overflow-hidden rounded-[10px] border border-[#E5E5E5] bg-white p-6 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10),0_4px_6px_-4px_rgba(0,0,0,0.10)]"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-6 top-6 flex h-4 w-4 items-center justify-center z-10 text-[#0A0A0A] opacity-70"
        >
          <X size={16} />
        </button>

        <div className="flex flex-col gap-6 flex-1 min-h-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex h-4 w-4 items-center justify-center text-[#737373]"
            >
              <ArrowLeft size={16} />
            </button>
          )}

          <h2 className="text-[18px] font-semibold leading-[18px] text-[#0A0A0A]">
            Manage Charges & Securities
          </h2>

          <div className="flex-1 overflow-y-auto min-h-0 pr-1 flex flex-col gap-6">
            
            <div className="rounded-[8px] bg-[#F9FAFB] p-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-2">
                  <span className="text-[16px] font-medium leading-6 text-[#0A0A0A]">{securityRole}</span>
                  <span className="text-[12px] font-normal leading-4 text-[#737373]">
                    {chargeMode} • {distributionRights}
                  </span>
                </div>

                <div className="flex gap-2">
                  <div className="flex items-center gap-1 rounded-[8px] bg-[#F3F4F6] px-2 py-1 text-xs font-medium text-[#4B5563]">
                    <Layers size={16} className="text-[#3B82F6]" />
                    {assetCount} Assets
                  </div>

                  <div className="flex items-center gap-1 rounded-[8px] bg-[#F3F4F6] px-2 py-1 text-xs font-medium text-[#4B5563]">
                    <Landmark size={16} className="text-[#22C55E]" />
                    {loanCount} Loan{loanCount > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ActionCard
                icon={<Link2 size={16} className="text-[#0A0A0A]" />}
                title="Add Existing Asset"
                description="Link an already created asset to this charge"
                onClick={() => setView(ManageChargeView.ADD_EXISTING_ASSET)}
              />
              <ActionCard
                icon={<Plus size={16} className="text-[#0A0A0A]" />}
                title="Add New Asset"
                description="Create a new asset and link it to this charge"
                onClick={() => setView(ManageChargeView.ADD_NEW_ASSET)}
              />
              <ActionCard
                icon={<Landmark size={16} className="text-[#0A0A0A]" />}
                title="Add Loan"
                description="Link another loan account covered by this charge"
                onClick={() => setView(ManageChargeView.ADD_LOAN)}
              />
            </div>

            <Section title="Assets Covered">
              {Object.entries(groupedAssets).map(([assetClass, assets]) => (
                <div key={assetClass} className="flex flex-col gap-0.5">
                  <div className="text-[12px] font-medium text-[#737373]">
                    {ASSET_CLASS_LABELS[assetClass as keyof typeof ASSET_CLASS_LABELS]}
                  </div>

                  <div className="flex flex-col">
                    {assets.map(asset => (
                      <div key={asset.asset_id} className="flex flex-col gap-0.5 py-2">
                        <div className="text-[14px] font-normal leading-5 text-[#0A0A0A]">
                          {asset.asset_name}
                        </div>
                        <div className="text-[12px] font-normal leading-4 text-[#737373]">
                          {asset.ext_asset_id} • {ASSET_TYPE_LABELS[asset.asset_type]}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </Section>

            <Section title="Loans Covered">
              <div className="flex flex-col">
                {creditAccounts.map((account, index) => (
                  <div
                    key={account.loan_reference_id}
                    className={`flex flex-col gap-0.5 pb-2 ${index === 0 ? "" : "pt-2"}`}
                  >
                    <div className="text-[14px] font-normal leading-5 text-[#0A0A0A]">
                      {account.account_name || "Name unavailable"}
                    </div>
                    <div className="text-[12px] leading-4 text-[#737373]">
                      {account.ext_account_number}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageChargeModal;

const ActionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}> = ({ icon, title, description, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-start rounded-[8px] border border-[#E5E5E5] bg-white p-3 text-left 
      shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] hover:bg-[#FAFAFA] transition-colors w-full min-h-[84px] gap-2"
  >
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-[14px] font-medium text-[#171717] leading-5">{title}</span>
    </div>
    <p className="text-[12px] text-[#737373] leading-4">{description}</p>
  </button>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-[14px] font-medium text-[#0A0A0A] leading-5">{title}</h3>
    <div className="rounded-[8px] bg-[#F9FAFB] p-3 flex flex-col gap-2">{children}</div>
  </div>
);
